import './Podcasts.css';
import { useState, useEffect, useRef } from 'react';
import { FaSpotify, FaYoutube, FaPodcast, FaListUl, FaChevronLeft, FaChevronRight, FaPlay, FaPause, FaRedo, FaUndo, FaChevronDown, FaChevronUp, FaCheckCircle, FaShare, FaTimes, FaBell } from 'react-icons/fa';
import { SiApplepodcasts, SiYoutubemusic } from 'react-icons/si';
import { MdGraphicEq } from 'react-icons/md';
import RSSParser from './rssParser';
import LazyImage from './LazyImage';
import {
    enableEpisodeNotifications,
    disableEpisodeNotifications,
    getNotificationStatus,
    isPushSupported,
    isSafariIOSWithoutPWA,
} from './notificationService';
import InstallBanner from './InstallBanner';

function Podcasts({ onLoadComplete }) {
    const [, setSelectedPlatform] = useState(null);
    const [episodes, setEpisodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentEpisode, setCurrentEpisode] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioElement, setAudioElement] = useState(null);
    const [podcastInfo, setPodcastInfo] = useState({});
    const [showEpisodes, setShowEpisodes] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlayerCollapsed, setIsPlayerCollapsed] = useState(false);
    const [episodesPerPage, setEpisodesPerPage] = useState(5);
    const [episodeProgress, setEpisodeProgress] = useState({});
    const [highlightedEpisode, setHighlightedEpisode] = useState(null);
    const [notificationStatus, setNotificationStatus] = useState({
        supported: false,
        permission: 'default',
        enabled: false,
        blocked: true
    });
    const [notificationBusy, setNotificationBusy] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
    const progressBarRef = useRef(null);
    const episodesListRef = useRef(null);
    const hasFetchedRef = useRef(false);
    const highlightedEpisodeRef = useRef(null);
    const hasInitializedNotificationsRef = useRef(false);
    const isNotificationPermissionBlocked = notificationStatus.supported && notificationStatus.blocked;
    const canShowNotificationActions = notificationStatus.supported && !isNotificationPermissionBlocked;

    // Configure seu RSS fixo aqui
    const podcastRSS = "https://anchor.fm/s/db4c6310/podcast/rss";

    // Plataformas de podcasts disponíveis
    const platforms = [
        {
            name: "Spotify",
            icon: <FaSpotify />,
            url: "https://open.spotify.com/show/35z2TFjOm3UXbevOeNGwXb?si=KmtPYOXgTU-XU1RcAzt_lA",
            color: "#1DB954"
        },
        {
            name: "YouTube",
            icon: <FaYoutube />,
            url: `https://www.youtube.com/playlist?list=PLhv1yVlzAc04e7YFKfS9eavFFZheuM8Xr`,
            color: "#FF0000"
        },
        {
            name: "YouTube Music",
            icon: <SiYoutubemusic />,
            url: `https://music.youtube.com/playlist?list=PLhv1yVlzAc04e7YFKfS9eavFFZheuM8Xr`,
            color: "#FF0000"
        },
        {
            name: "Apple Podcasts",
            icon: <SiApplepodcasts />,
            url: `${podcastRSS.replace("https://", "podcast://")}`,
            color: "#9333EA"
        }
    ];

    // PodLink separado como plataforma agregadora
    const podlinkPlatform = {
        name: "Demais Plataformas",
        icon: <FaPodcast />,
        url: `https://pod.link/aHR0cHM6Ly9hbmNob3IuZm0vcy9kYjRjNjMxMC9wb2RjYXN0L3Jzcw`,
        color: "#9333EA"
    };

    // Canal do YouTube
    const youtubeChannel = {
        name: "Canal no YouTube",
        icon: <FaYoutube />,
        url: "https://www.youtube.com/@casavivaitaipava",
        color: "#FF0000"
    };

    // Encontrar episódio por GUID
    const findEpisodeByGuid = (guid) => {
        return episodes.find(ep => ep.guid === guid);
    };

    // Calcular página do episódio
    const calculateEpisodePage = (episodeIndex) => {
        return Math.ceil((episodeIndex + 1) / episodesPerPage);
    };

    // Verificar URL para episódio compartilhado
    useEffect(() => {
        if (episodes.length > 0) {
            const urlParams = new URLSearchParams(window.location.search);
            const episodeGuid = urlParams.get('episode');

            if (episodeGuid) {
                const episode = findEpisodeByGuid(episodeGuid);
                if (episode) {
                    const episodeIndex = episodes.indexOf(episode);
                    const targetPage = calculateEpisodePage(episodeIndex);

                    // Abrir seção de episódios
                    setShowEpisodes(true);
                    // Navegar para a página correta
                    setCurrentPage(targetPage);
                    // Destacar episódio
                    setHighlightedEpisode(episodeGuid);
                    // Iniciar reprodução automaticamente
                    handlePlayEpisode(episode);

                    // Aguardar renderização e rolar para o episódio
                    setTimeout(() => {
                        if (highlightedEpisodeRef.current) {
                            highlightedEpisodeRef.current.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }

                        // Remover destaque após alguns segundos
                        setTimeout(() => {
                            setHighlightedEpisode(null);
                        }, 3000);
                    }, 300);

                    // Remover querystring da URL
                    window.history.replaceState({}, document.title, window.location.pathname);
                }
            }
        }
    }, [episodes, episodesPerPage]);

    // Função para compartilhar episódio
    const handleShareEpisode = async (episode) => {
        const shareUrl = `${window.location.origin}${window.location.pathname}?episode=${episode.guid}`;

        const shareData = {
            title: episode.title,
            text: `Confira este episódio: ${episode.title}`,
            url: shareUrl
        };

        try {
            if (navigator.share) {
                // Usar Web Share API (mobile)
                await navigator.share(shareData);
            } else {
                // Fallback: copiar para clipboard
                await navigator.clipboard.writeText(shareUrl);
                alert('Link copiado para a área de transferência!');
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Erro ao compartilhar:', error);
                // Fallback: copiar para clipboard
                try {
                    await navigator.clipboard.writeText(shareUrl);
                    alert('Link copiado para a área de transferência!');
                } catch (clipboardError) {
                    console.error('Erro ao copiar:', clipboardError);
                }
            }
        }
    };

    // Carregar progresso salvo do localStorage
    useEffect(() => {
        const savedProgress = localStorage.getItem('podcastProgress');
        if (savedProgress) {
            try {
                setEpisodeProgress(JSON.parse(savedProgress));
            } catch (error) {
                console.error('Erro ao carregar progresso:', error);
            }
        }
    }, []);

    useEffect(() => {
        // Previne execução duplicada no StrictMode durante desenvolvimento.
        if (hasInitializedNotificationsRef.current) return;
        hasInitializedNotificationsRef.current = true;

        const initializeNotifications = async () => {
            if (isSafariIOSWithoutPWA()) {
                setNotificationStatus({
                    supported: false,
                    permission: 'default',
                    enabled: false,
                    blocked: false
                });
                setNotificationMessage(
                    'No Safari (iOS), instale este app na Tela de Início para receber notificações.'
                );
                return;
            }

            if (!isPushSupported()) {
                setNotificationStatus({
                    supported: false,
                    permission: 'default',
                    enabled: false,
                    blocked: true
                });
                return;
            }

            const status = await getNotificationStatus();
            setNotificationStatus(status);

            const permissionBlocked = status.supported && status.blocked;

            if (permissionBlocked) {
                setShowNotificationPrompt(false);
                return;
            }

            if (status.supported && status.permission === 'default' && !status.enabled) {
                if (!sessionStorage.getItem('cv_notif_prompt_dismissed')) {
                    setShowNotificationPrompt(true);
                }
            }
        };

        initializeNotifications();
    }, []);

    const handleToggleNotifications = async () => {
        setNotificationBusy(true);
        setNotificationMessage('');

        try {
            if (notificationStatus.enabled) {
                const nextStatus = await disableEpisodeNotifications();
                setNotificationStatus(nextStatus);
                setNotificationMessage('Notificações desativadas para este navegador.');
                setShowNotificationPrompt(false);
                return;
            }

            const nextStatus = await enableEpisodeNotifications();
            setNotificationStatus(nextStatus);
            if (nextStatus.enabled) {
                setNotificationMessage('Notificações ativadas. Você será avisado(a) quando sair novo episódio.');
            } else if (nextStatus.blocked) {
                setNotificationMessage('Permissão bloqueada no navegador. Reative nas configurações do site.');
            } else {
                setNotificationMessage('A permissão não foi concedida agora.');
            }
            setShowNotificationPrompt(false);
        } catch (error) {
            console.error('Erro ao alterar notificações:', error);

            if (error.message?.startsWith('backend-error')) {
                const status = error.message.split(':')[1];
                setNotificationMessage(`Falha no servidor (${status}). Tente novamente mais tarde.`);
            } else if (error.message === 'missing-vapid-key') {
                setNotificationMessage('Configuração pendente: chave VAPID não configurada para este ambiente.');
                } else if (error.message === 'missing-api-url') {
                    setNotificationMessage('Configuração pendente: URL da API de notificações não configurada para este ambiente.');
            } else if (Notification.permission === 'denied') {
                setNotificationMessage('Permissão negada no navegador. Reative nas configurações do site.');
            } else {
                setNotificationMessage('Não foi possível atualizar suas notificações agora.');
            }

            const refreshedStatus = await getNotificationStatus();
            setNotificationStatus(refreshedStatus);
            if (refreshedStatus.blocked) {
                setShowNotificationPrompt(false);
            }
        } finally {
            setNotificationBusy(false);
        }
    };

    // Salvar progresso no localStorage
    const saveProgress = (episodeGuid, currentTime, duration) => {
        if (!episodeGuid || !duration) return;

        const existingProgress = episodeProgress[episodeGuid];
        const percentage = (currentTime / duration) * 100;
        const isCompleted = percentage >= 99;

        // Manter isCompleted como true se já foi completado anteriormente
        const wasCompletedBefore = existingProgress?.isCompleted || false;

        const updatedProgress = {
            ...episodeProgress,
            [episodeGuid]: {
                currentTime: currentTime,
                duration,
                percentage,
                isCompleted: wasCompletedBefore || isCompleted,
                lastPlayed: new Date().toISOString()
            }
        };

        setEpisodeProgress(updatedProgress);
        localStorage.setItem('podcastProgress', JSON.stringify(updatedProgress));
    };

    // Função para fechar o player
    const handleClosePlayer = () => {
        if (audioElement) {
            audioElement.pause();
            // Salvar progresso ao fechar
            if (currentEpisode?.guid) {
                saveProgress(currentEpisode.guid, audioElement.currentTime, audioElement.duration);
            }
        }

        // Limpar estados
        setAudioElement(null);
        setCurrentEpisode(null);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setIsPlayerCollapsed(false);
    };

    // Buscar episódios do RSS usando parser com fallback
    useEffect(() => {
        // Prevenir múltiplas chamadas (especialmente em React.StrictMode)
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        const fetchPodcast = async () => {
            try {
                const parser = new RSSParser();
                const data = await parser.parse(podcastRSS);

                if (data.status !== 'ok') {
                    throw new Error('Erro ao carregar feed RSS');
                }

                const podcastData = {
                    title: data.feed.title,
                    description: data.feed.description,
                    image: data.feed.image
                };

                const episodesData = data.items.map(item => ({
                    title: item.title,
                    description: item.description,
                    audioUrl: item.enclosure?.link,
                    pubDate: item.pubDate,
                    image: item.thumbnail || item.image || item.enclosure?.thumbnail || data.feed.image,
                    link: item.link,
                    duration: item.duration,
                    guid: item.guid
                }));

                setPodcastInfo(podcastData);
                setEpisodes(episodesData);
                setLoading(false);

                // Notificar que o carregamento foi concluído
                if (onLoadComplete) {
                    onLoadComplete({
                        podcast: podcastData,
                        episodes: episodesData
                    });
                }
            } catch (error) {
                console.error('Erro ao buscar RSS:', error);
                setLoading(false);

                // Notificar mesmo em caso de erro
                if (onLoadComplete) {
                    onLoadComplete({ error: true });
                }
            }
        };

        fetchPodcast();
    }, [podcastRSS, onLoadComplete]);

    // Configurar event listeners do áudio
    useEffect(() => {
        if (audioElement) {
            const updateTime = () => {
                setCurrentTime(audioElement.currentTime);
                // Salvar progresso a cada 5 segundos
                if (audioElement.currentTime % 5 < 0.5) {
                    saveProgress(currentEpisode?.guid, audioElement.currentTime, audioElement.duration);
                }
            };
            const updateDuration = () => setDuration(audioElement.duration);
            const handleEnded = () => {
                // Marcar como completo ao terminar
                saveProgress(currentEpisode?.guid, audioElement.duration, audioElement.duration);
                // Fechar o player automaticamente
                handleClosePlayer();
            };

            audioElement.addEventListener('timeupdate', updateTime);
            audioElement.addEventListener('loadedmetadata', updateDuration);
            audioElement.addEventListener('ended', handleEnded);

            return () => {
                audioElement.removeEventListener('timeupdate', updateTime);
                audioElement.removeEventListener('loadedmetadata', updateDuration);
                audioElement.removeEventListener('ended', handleEnded);
                // Salvar progresso ao desmontar
                if (currentEpisode?.guid) {
                    saveProgress(currentEpisode.guid, audioElement.currentTime, audioElement.duration);
                }
            };
        }
    }, [audioElement, currentEpisode]);

    const handlePlatformClick = (platform) => {
        setSelectedPlatform(platform.name);
        window.open(platform.url, '_blank');
    };

    const handlePlayEpisode = (episode) => {
        if (currentEpisode?.guid === episode.guid) {
            // Se é o mesmo episódio, apenas retorna (não faz nada)
            return;
        } else {
            // Novo episódio
            if (audioElement) {
                audioElement.pause();
            }

            const audio = new Audio(episode.audioUrl);

            // Verificar se há progresso salvo
            const savedProgress = episodeProgress[episode.guid];
            // Se não está completo e tem progresso, continua de onde parou
            // Se está completo, permite começar do zero (não carrega o progresso anterior)
            if (savedProgress && !savedProgress.isCompleted && savedProgress.currentTime > 0) {
                // Aguardar o áudio carregar os metadados antes de definir o tempo
                audio.addEventListener('loadedmetadata', () => {
                    audio.currentTime = savedProgress.currentTime;
                }, { once: true });
            }

            // Tentar reproduzir e tratar erro de autoplay
            const playPromise = audio.play();

            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        setIsPlaying(true);
                    })
                    .catch((error) => {
                        setIsPlaying(false);
                    });
            }

            setAudioElement(audio);
            setCurrentEpisode(episode);
            setIsPlayerCollapsed(false); // Expandir player ao iniciar novo episódio
        }
    };

    const handlePlayPause = () => {
        if (!audioElement) return;

        if (isPlaying) {
            audioElement.pause();
            setIsPlaying(false);
            // Salvar progresso ao pausar
            if (currentEpisode?.guid) {
                saveProgress(currentEpisode.guid, audioElement.currentTime, audioElement.duration);
            }
        } else {
            audioElement.play();
            setIsPlaying(true);
        }
    };

    const handleSkipForward = () => {
        if (!audioElement) return;
        audioElement.currentTime = Math.min(audioElement.currentTime + 15, duration);
    };

    const handleSkipBackward = () => {
        if (!audioElement) return;
        audioElement.currentTime = Math.max(audioElement.currentTime - 15, 0);
    };

    const handleProgressClick = (e) => {
        if (!audioElement || !progressBarRef.current) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        audioElement.currentTime = pos * duration;
    };

    const togglePlayerCollapse = () => {
        setIsPlayerCollapsed(!isPlayerCollapsed);
    };

    const formatTime = (seconds) => {
        if (isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatDuration = (duration) => {
        if (!duration) return '';

        // Se vier no formato HH:MM:SS ou MM:SS, retorne direto
        if (typeof duration === 'string' && duration.includes(':')) {
            const parts = duration.split(':');
            if (parts.length === 3) {
                const hours = parseInt(parts[0]);
                return hours > 0 ? duration : `${parseInt(parts[1])}:${parts[2]}`;
            }
            return duration;
        }

        // Se vier em segundos
        const seconds = parseInt(duration);
        if (isNaN(seconds)) return '';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const getEpisodeProgress = (episodeGuid) => {
        return episodeProgress[episodeGuid];
    };

    const truncateHtml = (html, maxLength) => {
        if (!html) return '';
        const text = html.replace(/<[^>]*>/g, '');
        if (text.length <= maxLength) return html;

        const truncated = text.substring(0, maxLength);
        return truncated + '...';
    };

    // Cálculos de paginação
    const indexOfLastEpisode = currentPage * episodesPerPage;
    const indexOfFirstEpisode = indexOfLastEpisode - episodesPerPage;
    const currentEpisodes = episodes.slice(indexOfFirstEpisode, indexOfLastEpisode);
    const totalPages = Math.ceil(episodes.length / episodesPerPage);

    const handlePageChange = (pageNumber = 1) => {
        setCurrentPage(pageNumber);
        // Rolar para a lista de episódios ao invés do topo
        if (episodesListRef.current) {
            episodesListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleEpisodesPerPageChange = (value) => {
        setEpisodesPerPage(Number(value));
        setCurrentPage(1); // Resetar para a primeira página
        // Rolar para a lista de episódios
        if (episodesListRef.current) {
            episodesListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Gerar números de página para exibição
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                pages.push(currentPage - 1);
                pages.push(currentPage);
                pages.push(currentPage + 1);
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    return (
        <div className="podcasts-container">
            <InstallBanner />
            {/* Header com info do podcast */}
            {!loading && podcastInfo.title && (
                <div className="podcast-header">
                    {podcastInfo.image && (
                        <LazyImage
                            src={podcastInfo.image}
                            alt={podcastInfo.title}
                            className="podcast-cover"
                        />
                    )}
                    <div className="podcast-info">
                        <h1>{podcastInfo.title}</h1>
                        <p className="podcast-description">{podcastInfo.description}</p>
                    </div>

                            {/* Modal de Notificações - Bloqueante */}
                            {showNotificationPrompt && canShowNotificationActions && (
                                <div className="notifications-modal-overlay">
                                    <div className="notifications-modal">
                                        <div className="notifications-modal-header">
                                            <h2>Notificações de Episódios</h2>
                                            <p>Deseja receber notificações quando sair novo episódio?</p>
                                        </div>
                                        <div className="notifications-modal-buttons">
                                            <button
                                                className="notifications-modal-button notifications-modal-button-primary"
                                                onClick={handleToggleNotifications}
                                                disabled={notificationBusy}
                                            >
                                                {notificationBusy ? 'Processando...' : 'Ativar agora'}
                                            </button>
                                            <button
                                                className="notifications-modal-button notifications-modal-button-secondary"
                                                onClick={() => {
                                                    sessionStorage.setItem('cv_notif_prompt_dismissed', '1');
                                                    setShowNotificationPrompt(false);
                                                }}
                                                disabled={notificationBusy}
                                            >
                                                Agora não
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                </div>
            )}

            {/* Plataformas */}
            <div className="section">
                <h2>Escolha onde ouvir</h2>
                <p className="podcasts-subtitle">
                    Disponível nas principais plataformas de podcasts
                </p>

                <div className="platforms-grid">
                    {platforms.map((platform) => (
                        <button
                            key={platform.name}
                            className="platform-button"
                            style={{ '--platform-color': platform.color }}
                            onClick={() => handlePlatformClick(platform)}
                        >
                            <span className="platform-icon">{platform.icon}</span>
                            <span className="platform-name">{platform.name}</span>
                        </button>
                    ))}
                </div>

                {/* Seção PodLink destacada */}
                <div className="podlink-section">
                    <div className="podlink-header">
                        <div className="podlink-icon-wrapper">
                            {podlinkPlatform.icon}
                        </div>
                        <div className="podlink-text">
                            <h3>Não encontrou sua plataforma favorita?</h3>
                            <p>
                                Use o <strong>PodLink</strong> para acessar nosso podcast em mais de 20 plataformas diferentes,
                                incluindo Deezer, Amazon Music, Podcast Addict, Castbox, Overcast e muitas outras!
                            </p>
                        </div>
                    </div>
                    <button
                        className="podlink-button"
                        style={{ '--platform-color': podlinkPlatform.color }}
                        onClick={() => handlePlatformClick(podlinkPlatform)}
                    >
                        <span className="platform-icon">{podlinkPlatform.icon}</span>
                        <span className="platform-name">Ver demais plataformas</span>
                    </button>
                </div>

                {/* Seção YouTube destacada */}
                <div className="podlink-section" style={{ marginTop: '2rem' }}>
                    <div className="podlink-header">
                        <div className="podlink-icon-wrapper" style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)' }}>
                            <FaYoutube style={{ color: '#FF0000' }} />
                        </div>
                        <div className="podlink-text">
                            <h3>Inscreva-se no nosso canal do YouTube!</h3>
                            <p>
                                Não perca nenhum conteúdo! <strong>Inscreva-se</strong> no canal{' '}
                                <strong>@casavivaitaipava</strong>, ative o{' '}
                                <FaBell style={{ verticalAlign: 'middle', color: '#FF0000' }} /> <strong>sininho de notificações</strong>{' '}
                                e fique por dentro de todos os episódios novos, vídeos, informações e muito mais!
                            </p>
                        </div>
                    </div>
                    <button
                        className="podlink-button"
                        style={{ '--platform-color': youtubeChannel.color }}
                        onClick={() => handlePlatformClick(youtubeChannel)}
                    >
                        <span className="platform-icon">{youtubeChannel.icon}</span>
                        <span className="platform-name">Inscrever-se no canal</span>
                    </button>
                </div>

                <div className="podlink-section notifications-card" style={{ marginTop: '2rem' }}>
                    <div className="notifications-header">
                        <div className="notifications-icon-wrap">
                            <FaBell />
                        </div>
                        <div>
                            <h3>Notificações de novos episódios</h3>
                            <p>
                                Receba avisos sempre que um novo episódio for publicado.
                            </p>
                        </div>
                    </div>

                    {!notificationStatus.supported && isSafariIOSWithoutPWA() ? (
                        <p className="notifications-status muted">
                            No Safari (iOS), instale este app na Tela de Início para
                            receber notificações de novos episódios.
                        </p>
                    ) : !notificationStatus.supported ? (
                        <p className="notifications-status muted">
                            Seu navegador não suporta Web Push nesta plataforma.
                        </p>
                    ) : isNotificationPermissionBlocked ? (
                        <p className="notifications-status muted">
                            Notificações bloqueadas neste navegador. Para voltar a receber alertas, habilite nas permissões do site.
                        </p>
                    ) : (
                        <>
                            <p className="notifications-status">
                                Status atual:{' '}
                                <strong>
                                    {notificationStatus.enabled
                                        ? 'Ativado'
                                        : 'Desativado'}
                                </strong>
                            </p>

                            <button
                                className="podlink-button notifications-toggle-button"
                                onClick={handleToggleNotifications}
                                disabled={notificationBusy}
                            >
                                {notificationBusy
                                    ? 'Processando...'
                                    : notificationStatus.enabled
                                        ? 'Desativar notificações'
                                        : 'Ativar notificações'}
                            </button>

                        </>
                    )}

                    {notificationMessage && (
                        <p className="notifications-feedback">{notificationMessage}</p>
                    )}
                </div>
            </div>

            {/* Player fixo */}
            {currentEpisode && (
                <div className={`fixed-player ${isPlayerCollapsed ? 'collapsed' : ''}`}>
                    <div className="player-content">
                        {!isPlayerCollapsed ? (
                            <>
                                {/* Player Expandido */}
                                {currentEpisode.image && (
                                    <LazyImage
                                        src={currentEpisode.image}
                                        alt={currentEpisode.title}
                                        className="player-thumbnail"
                                    />
                                )}
                                <div className="player-info">
                                    <div className="player-title">{currentEpisode.title}</div>
                                    <div className="player-time">
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </div>
                                </div>

                                <div className="player-controls-section">
                                    {/* Barra de progresso */}
                                    <div
                                        className="progress-bar-container"
                                        ref={progressBarRef}
                                        onClick={handleProgressClick}
                                    >
                                        <div
                                            className="progress-bar"
                                            style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                                        >
                                            <div className="progress-handle"></div>
                                        </div>
                                    </div>

                                    {/* Controles */}
                                    <div className="player-controls">
                                        <button
                                            className="control-button control-button-small"
                                            onClick={handleSkipBackward}
                                            title="Voltar 15s"
                                            disabled={!audioElement}
                                        >
                                            <FaUndo size={14} />
                                            <span className="skip-label">15</span>
                                        </button>

                                        <button
                                            className="control-button control-button-main"
                                            onClick={handlePlayPause}
                                            title={isPlaying ? 'Pausar' : 'Reproduzir'}
                                            disabled={!audioElement}
                                        >
                                            {isPlaying ? <FaPause size={20} /> : <FaPlay size={20} />}
                                        </button>

                                        <button
                                            className="control-button control-button-small"
                                            onClick={handleSkipForward}
                                            title="Avançar 15s"
                                            disabled={!audioElement}
                                        >
                                            <FaRedo size={14} />
                                            <span className="skip-label">15</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Botões de controle do player */}
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    {/* Botão de colapsar - versão expandida */}
                                    <button
                                        className="collapse-button-expanded"
                                        onClick={togglePlayerCollapse}
                                        title="Minimizar player"
                                    >
                                        <FaChevronDown size={20} />
                                    </button>

                                    {/* Botão de fechar */}
                                    <button
                                        className="collapse-button-expanded"
                                        onClick={handleClosePlayer}
                                        title="Fechar player"
                                        style={{ color: '#ef4444' }}
                                    >
                                        <FaTimes size={20} />
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Player Colapsado */}
                                <div className="player-collapsed-content">
                                    {currentEpisode.image && (
                                        <LazyImage
                                            src={currentEpisode.image}
                                            alt={currentEpisode.title}
                                            className="player-thumbnail-collapsed"
                                        />
                                    )}

                                    <div className="player-info-collapsed">
                                        <div className="player-title-collapsed">{currentEpisode.title}</div>
                                        <div className="player-time-collapsed">
                                            {formatTime(currentTime)} / {formatTime(duration)}
                                        </div>
                                    </div>

                                    {/* Barra de progresso mini */}
                                    <div
                                        className="progress-bar-mini"
                                        ref={progressBarRef}
                                        onClick={handleProgressClick}
                                    >
                                        <div
                                            className="progress-fill-mini"
                                            style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                                        />
                                    </div>

                                    <div className="player-controls-collapsed">
                                        <button
                                            className="control-button-mini"
                                            onClick={handleSkipBackward}
                                            title="Voltar 15s"
                                            disabled={!audioElement}
                                        >
                                            <FaUndo size={16} />
                                        </button>

                                        <button
                                            className="control-button-mini control-button-play-mini"
                                            onClick={handlePlayPause}
                                            title={isPlaying ? 'Pausar' : 'Reproduzir'}
                                            disabled={!audioElement}
                                        >
                                            {isPlaying ? <FaPause size={18} /> : <FaPlay size={18} />}
                                        </button>

                                        <button
                                            className="control-button-mini"
                                            onClick={handleSkipForward}
                                            title="Avançar 15s"
                                            disabled={!audioElement}
                                        >
                                            <FaRedo size={16} />
                                        </button>
                                    </div>

                                    {/* Botões de controle do player colapsado */}
                                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        {/* Botão de expandir - versão colapsada */}
                                        <button
                                            className="collapse-button-mini"
                                            onClick={togglePlayerCollapse}
                                            title="Expandir player"
                                        >
                                            <FaChevronUp size={20} />
                                        </button>

                                        {/* Botão de fechar */}
                                        <button
                                            className="collapse-button-mini"
                                            onClick={handleClosePlayer}
                                            title="Fechar player"
                                            style={{ color: '#ef4444' }}
                                        >
                                            <FaTimes size={20} />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Botão para mostrar/ocultar episódios */}
            <div className="section">
                <div style={{ marginBottom: '2rem' }}>
                    <button
                        className="platform-button toggle-episodes-button"
                        style={{ '--platform-color': '#8B5CF6' }}
                        onClick={() => {
                            setShowEpisodes(!showEpisodes);
                            if (!showEpisodes) {
                                setCurrentPage(1);
                            }
                        }}
                    >
                        <span className="platform-icon"><FaListUl /></span>
                        <span className="platform-name">
                            {showEpisodes ? 'Ocultar episódios' : 'Ver episódios'}
                        </span>
                    </button>
                </div>

                {/* Lista de episódios */}
                {showEpisodes && (
                    <>
                        {loading ? (
                            <div className="loading">Carregando episódios...</div>
                        ) : episodes.length === 0 ? (
                            <div className="loading">Nenhum episódio encontrado</div>
                        ) : (
                            <>
                                <div className="episodes-list" ref={episodesListRef}>
                                    {currentEpisodes.map((episode, index) => {
                                        const isCurrentEpisode = currentEpisode?.guid === episode.guid;
                                        const progress = getEpisodeProgress(episode.guid);
                                        const isCompleted = progress?.isCompleted;
                                        const hasProgress = progress && !isCompleted && progress.percentage > 0;
                                        const isHighlighted = highlightedEpisode === episode.guid;

                                        return (
                                            <div
                                                key={indexOfFirstEpisode + index}
                                                className={`episode-card ${isCurrentEpisode ? 'playing' : ''} ${isCompleted ? 'completed' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                                                ref={isHighlighted ? highlightedEpisodeRef : null}
                                            >
                                                {episode.image && (
                                                    <div style={{ position: 'relative' }}>
                                                        <LazyImage
                                                            src={episode.image}
                                                            alt={episode.title}
                                                            className="episode-thumbnail"
                                                        />
                                                        {isCompleted && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '0.5rem',
                                                                right: '0.5rem',
                                                                backgroundColor: 'rgba(34, 197, 94, 0.9)',
                                                                borderRadius: '50%',
                                                                padding: '0.25rem',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <FaCheckCircle size={16} color="white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="episode-content">
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                                                        <h3 className="episode-title" style={{ flex: 1 }}>
                                                            {episode.title}
                                                            {isCompleted && (
                                                                <span style={{
                                                                    marginLeft: '0.5rem',
                                                                    color: '#22c55e',
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 'normal'
                                                                }}>
                                                                    ✓ Completo
                                                                </span>
                                                            )}
                                                        </h3>
                                                        <button
                                                            onClick={() => handleShareEpisode(episode)}
                                                            style={{
                                                                background: 'transparent',
                                                                border: 'none',
                                                                color: '#8B5CF6',
                                                                cursor: 'pointer',
                                                                padding: '0.5rem',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                transition: 'all 0.2s',
                                                                borderRadius: '0.375rem'
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                            title="Compartilhar"
                                                        >
                                                            <FaShare size={18} />
                                                        </button>
                                                    </div>
                                                    <p className="episode-date">
                                                        {formatDate(episode.pubDate)}
                                                        {episode.duration && <span> • {formatDuration(episode.duration)}</span>}
                                                    </p>
                                                    {hasProgress && (
                                                        <div style={{
                                                            marginTop: '0.5rem',
                                                            marginBottom: '0.5rem'
                                                        }}>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.5rem',
                                                                marginBottom: '0.25rem'
                                                            }}>
                                                                <div style={{
                                                                    flex: 1,
                                                                    height: '4px',
                                                                    backgroundColor: '#374151',
                                                                    borderRadius: '2px',
                                                                    overflow: 'hidden'
                                                                }}>
                                                                    <div style={{
                                                                        width: `${progress.percentage}%`,
                                                                        height: '100%',
                                                                        backgroundColor: '#8B5CF6',
                                                                        transition: 'width 0.3s ease'
                                                                    }} />
                                                                </div>
                                                                <span style={{
                                                                    fontSize: '0.75rem',
                                                                    color: '#9CA3AF'
                                                                }}>
                                                                    {Math.round(progress.percentage)}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <p
                                                        className="episode-description"
                                                        dangerouslySetInnerHTML={{
                                                            __html: truncateHtml(episode.description, 150)
                                                        }}
                                                    />
                                                    {isCurrentEpisode ? (
                                                        <button
                                                            className="play-button playing-button"
                                                            disabled
                                                        >
                                                            <MdGraphicEq className="equalizer-icon" />
                                                            {isPlaying ? 'Reproduzindo' : 'Pausado'}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="play-button"
                                                            onClick={() => handlePlayEpisode(episode)}
                                                            disabled={!episode.audioUrl}
                                                        >
                                                            <FaPlay size={12} style={{ marginRight: '0.5rem' }} />
                                                            {isCompleted ? 'Ouvir novamente' : hasProgress ? 'Continuar' : 'Ouvir agora'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Controles de paginação e episódios por página */}
                                <div style={{ marginTop: '2rem' }}>
                                    {/* Paginação */}
                                    {totalPages > 1 && (
                                        <div className="pagination">
                                            <button
                                                className="pagination-nav-button"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                title="Página anterior"
                                            >
                                                <FaChevronLeft size={14} />
                                            </button>

                                            <div className="pagination-numbers">
                                                {getPageNumbers().map((pageNum, index) => (
                                                    pageNum === '...' ? (
                                                        <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                                                            ...
                                                        </span>
                                                    ) : (
                                                        <button
                                                            key={pageNum}
                                                            className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                                                            onClick={() => handlePageChange(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    )
                                                ))}
                                            </div>

                                            <button
                                                className="pagination-nav-button"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                title="Próxima página"
                                            >
                                                <FaChevronRight size={14} />
                                            </button>
                                        </div>
                                    )}

                                    {/* Dropdown de episódios por página */}
                                    <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                                        <select
                                            value={episodesPerPage}
                                            onChange={(e) => handleEpisodesPerPageChange(e.target.value)}
                                            style={{
                                                padding: '0.5rem 2.5rem 0.5rem 1rem',
                                                borderRadius: '0.5rem',
                                                border: '1px solid #374151',
                                                backgroundColor: '#1F2937',
                                                color: 'white',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                outline: 'none',
                                                appearance: 'none',
                                                backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'right 0.5rem center',
                                                backgroundSize: '1.5rem'
                                            }}
                                        >
                                            <option value={5}>5 itens por página</option>
                                            <option value={10}>10 itens por página</option>
                                            <option value={25}>25 itens por página</option>
                                            <option value={50}>50 itens por página</option>
                                        </select>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default Podcasts;